import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { ApiResponseStatus, baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface ExecuteChargeFromWalletResponse {
  status: ApiResponseStatus;
  message: string;
  data: Data;
}

export interface Data {
  processedInvoices: Array<ProcessedInvoice>;
  remainingBalance: number;
}

export interface ProcessedInvoice {
  invoiceId: string;
  amountPaid: number;
}

export interface ExecuteChargeFromWalletParams {
  parentId: string;
  studentId: string;
  invoiceIds?: Array<string>;
  /** Studio staff explicitly applying wallet to selected invoices before due date. */
  allowEarlyCollection?: boolean;
}

export const executeChargeFromWallet = async (
  params: ExecuteChargeFromWalletParams,
) => {
  const response = await baseAPI.post<ExecuteChargeFromWalletResponse>(
    'payment-processor/execute-charge-from-wallet',
    {
      ...params,
      allowEarlyCollection: params.allowEarlyCollection ?? true,
    },
  );
  return response.data;
};

export const useExecuteChargeFromWallet = () => {
  return useMutation({
    mutationFn: (params: ExecuteChargeFromWalletParams) =>
      executeChargeFromWallet(params),

    onSuccess: (response, params) => {
      if (response.status !== ApiResponseStatus.SUCCESS) {
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENT_SUBSCRIPTION_INVOICES, params.studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PARENT_WALLET_TRANSACTIONS, params.parentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.WALLET_BALANCE, params.parentId],
      });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === KEYS.STUDENT_PAYMENT_HISTORY,
      });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === KEYS.PARENT_PAYMENTS_HISTORY,
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.OUTSTANDING_BALANCE, params.studentId],
      });
      // Wallet charge pays invoices → refresh Dashboard V2 AR
      void queryClient.invalidateQueries({ queryKey: [KEYS.ACCOUNTS_RECEIVABLE] });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
      });
    },
  });
};
